import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Chip,
  Autocomplete,
  Box,
  FormControlLabel,
  Switch,
  Typography
} from '@mui/material';

const INTERACTION_TYPES = [
  'trade',
  'conversation',
  'quest_giver',
  'vendor',
  'tutorial',
  'story',
  'delivery',
  'other'
];

const NPCEditDialog = ({ open, npc, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    interactions: [],
    defaultConversationIds: [],
    isActive: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && npc) {
      setFormData({
        name: npc.name || '',
        interactions: parseJSON(npc.interactions),
        defaultConversationIds: parseJSON(npc.defaultConversationIds),
        isActive: npc.isActive !== undefined ? npc.isActive : true
      });
    } else if (open && !npc) {
      setFormData({
        name: '',
        interactions: [],
        defaultConversationIds: [],
        isActive: true
      });
    }
    setError(null);
  }, [open, npc]);

  const parseJSON = (jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return [];
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save NPC');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{npc ? 'Edit NPC' : 'Create New NPC'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {npc && (
            <Box>
              <Typography variant="caption" color="text.secondary">NPC ID</Typography>
              <Typography variant="body2" fontFamily="monospace">{npc.id}</Typography>
            </Box>
          )}

          <TextField
            label="NPC Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
            helperText="Unique identifier for this NPC (e.g., Wally, Trader, Larry)"
          />

          <Autocomplete
            multiple
            options={INTERACTION_TYPES}
            value={formData.interactions}
            onChange={(e, newValue) => setFormData({ ...formData, interactions: newValue })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Interaction Types"
                helperText="Types of interactions this NPC supports"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip key={index} label={option} {...getTagProps({ index })} />
              ))
            }
          />

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={formData.defaultConversationIds}
            onChange={(e, newValue) => setFormData({ ...formData, defaultConversationIds: newValue })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Default Conversation IDs"
                helperText="UUIDs of default conversations (optional, can be edited later)"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  label={option.substring(0, 8) + '...'}
                  {...getTagProps({ index })}
                  size="small"
                />
              ))
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="Active"
          />

          {error && (
            <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>
              {error}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NPCEditDialog;

